import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AttemptSummary } from '../types';
import { AttemptStatusBadge } from './AttemptStatusBadge';
import { ResumeAttemptButton } from './ResumeAttemptButton';
import { useTranslation } from '../../../i18n';
import { Pagination, usePagination } from '../../../components/ui/Pagination';

interface AttemptHistoryTableProps {
  attempts: AttemptSummary[];
  scenarioId?: string;
  loading?: boolean;
}

export const AttemptHistoryTable: React.FC<AttemptHistoryTableProps> = ({
  attempts,
  scenarioId,
  loading = false,
}) => {
  const { t, currentLanguage } = useTranslation();
  const navigate = useNavigate();

  const locale = currentLanguage === 'hi' ? 'hi-IN' : currentLanguage === 'es' ? 'es-ES' : 'en-US';

  const {
    currentPage,
    pageSize,
    totalItems,
    paginatedItems: paginatedAttempts,
    goToPage,
    setPageSize,
  } = usePagination(attempts, 1, 20);

  if (loading) {
    return (
      <div className="admin-loading-state">
        <div className="dashboard-spinner" />
        <p>{t('common.loading_records', undefined, 'Loading history records…')}</p>
      </div>
    );
  }

  if (attempts.length === 0) {
    return (
      <div className="admin-empty-state">
        <h3 style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: 600, color: 'var(--navy)' }}>
          {t('table.no_attempts', undefined, 'No attempts found')}
        </h3>
        <p style={{ margin: 0, fontSize: '13px', color: 'var(--muted)' }}>
          {t('table.no_attempts_desc', undefined, 'No attempts have been recorded for this scenario yet.')}
        </p>
      </div>
    );
  }

  const formatDuration = (sec: number) => {
    const mins = Math.floor(sec / 60);
    const s = sec % 60;
    return `${mins}m ${s < 10 ? '0' : ''}${s}s`;
  };

  return (
    <div className="admin-table-container">
      <table className="admin-table">
        <thead>
          <tr>
            <th>{t('table.attempt_num', undefined, 'Attempt #')}</th>
            <th>{t('table.status', undefined, 'Status')}</th>
            <th>{t('table.started', undefined, 'Started')}</th>
            <th>{t('table.duration', undefined, 'Duration')}</th>
            <th>{t('table.turns', undefined, 'Turns')}</th>
            <th>{t('table.score', undefined, 'Score')}</th>
            <th style={{ textAlign: 'right' }}>{t('table.actions', undefined, 'Actions')}</th>
          </tr>
        </thead>
        <tbody>
          {paginatedAttempts.map((attempt) => {
            const started = attempt.startedAt
              ? new Date(attempt.startedAt).toLocaleDateString(locale, {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : '—';

            return (
              <tr
                key={attempt.id}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/attempts/${attempt.id}`)}
              >
                <td style={{ fontWeight: 600, color: 'var(--navy)' }}>
                  {t('attempts.meta.attempt', undefined, 'Attempt')} #{attempt.attemptNumber}
                </td>
                <td>
                  <AttemptStatusBadge status={attempt.status} />
                </td>
                <td style={{ fontSize: '12.5px', color: 'var(--muted)' }}>
                  {started}
                </td>
                <td style={{ fontSize: '12.5px', fontFamily: 'monospace' }}>
                  {formatDuration(attempt.durationSeconds)}
                </td>
                <td style={{ fontSize: '12.5px' }}>
                  {attempt.currentTurn} / {attempt.maxTurns}
                </td>
                <td>
                  {attempt.score !== null && attempt.score !== undefined ? (
                    <strong style={{ color: attempt.score >= 70 ? 'var(--ok)' : 'var(--warn)' }}>{attempt.score}%</strong>
                  ) : (
                    <span style={{ color: 'var(--muted)', fontSize: '12px' }}>—</span>
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <div className="admin-table-actions" style={{ justifyContent: 'flex-end' }}>
                    {attempt.canResume && (
                      <ResumeAttemptButton
                        attemptId={attempt.id}
                        scenarioId={scenarioId || attempt.simulationId}
                        canResume={attempt.canResume}
                        size="sm"
                      />
                    )}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/attempts/${attempt.id}`);
                      }}
                      className="admin-btn admin-btn--secondary admin-btn--sm"
                    >
                      {t('table.view_details', undefined, 'View Details')}
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          pageSize={pageSize}
          onPageChange={goToPage}
          onPageSizeChange={setPageSize}
          showPageSizeSelector
          pageSizeOptions={[10, 20, 50, 100]}
          itemLabel={t('attempts.pagination_label', undefined, 'attempts')}
        />
      )}
    </div>
  );
};
